import React, { useEffect, useState } from "react";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { products as mockProducts, company } from "../mock";
import Reveal from "./Reveal";
import { getProducts } from "../api";

const waNumber = (company.whatsapp || "").replace(/[^0-9]/g, "");
const waLink = (name) =>
  `https://wa.me/${waNumber}?text=${encodeURIComponent(
    `Hi Bhawani Kitchen Equipment, I'm interested in the "${name}". Please share details, price and availability.`
  )}`;

export default function ProductRange() {
  const [items, setItems] = useState(mockProducts);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getProducts()
      .then((data) => {
        if (mounted && Array.isArray(data) && data.length) setItems(data);
      })
      .catch(() => {
        /* keep mock fallback */
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section id="products" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <p className="overline text-sm mb-1">Our</p>
            <h2 className="section-title text-3xl md:text-5xl">Product Range</h2>
          </div>
          <a
            href="#products"
            className="inline-flex items-center gap-2 text-[color:var(--brand-blue)] font-semibold hover:gap-3 transition-all"
          >
            {loading ? "Loading..." : `${items.length} Products`} <ArrowRight size={18} />
          </a>
        </Reveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {items.map((p, i) => (
            <Reveal key={p.id || p.name} delay={(i % 4) * 80}>
              <div className="card-hover group bg-white rounded-2xl border border-slate-100 overflow-hidden h-full flex flex-col">
                <div className="relative overflow-hidden bg-slate-50">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-44 md:h-52 w-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5 flex flex-col flex-1">
                  <h3 className="font-head font-semibold text-base md:text-lg mb-1.5" style={{ color: "var(--brand-navy)" }}>
                    {p.name}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed flex-1">{p.desc}</p>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    {p.id ? (
                      <Link
                        to={`/product/${p.id}`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-blue)] hover:gap-2.5 transition-all"
                      >
                        Learn more <ArrowRight size={15} />
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400">
                        Learn more <ArrowRight size={15} />
                      </span>
                    )}
                    <a
                      href={waLink(p.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Enquire on WhatsApp"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#25D366] hover:bg-[#1ebe57] px-3 py-2 rounded-full shadow-sm transition-colors"
                    >
                      <MessageCircle size={15} /> Enquiry
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
